// #tag: ADMIN RESULTS MANAGEMENT SCREEN
// #purpose: Content management interface for creating, editing, categorizing, and publishing exam scorecards and merit lists.
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Building2,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  ExternalLink,
  Plus,
  Sparkles,
  ToggleLeft,
  ToggleRight,
  Trophy,
} from 'lucide-react';
import {
  createAdminResult,
  fetchAdminResult,
  fetchAdminResults,
  patchAdminResult,
  type AdminResultRow,
} from '../../api/admin';
import { ApiError } from '../../api/client';
import {
  AdminDetailCard,
  AdminEmptyState,
  AdminKpiStrip,
  AdminPageLayout,
  AdminSearchInput,
  AdminStatusBadge,
  AdminTablePanel,
} from '../../components/admin/AdminPageLayout';
import { LoadingState, ErrorState } from '../../components/states/StateViews';

const CATEGORY_LABELS: Record<string, string> = {
  latest: 'Latest',
  state: 'State',
  central: 'Central',
  university: 'University',
  entrance: 'Entrance',
  recruitment: 'Recruitment',
};

function categoryLabel(category: string) {
  return CATEGORY_LABELS[category] ?? category;
}

function CreateResultModal({
  open,
  onClose,
  examCategories,
}: {
  open: boolean;
  onClose: () => void;
  examCategories: string[];
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    title: '',
    organization: '',
    category: 'central',
    examCategory: examCategories[0] ?? 'SSC',
    publishedAt: new Date().toISOString().slice(0, 10),
    isNew: true,
  });
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setForm({
      title: '',
      organization: '',
      category: 'central',
      examCategory: examCategories[0] ?? 'SSC',
      publishedAt: new Date().toISOString().slice(0, 10),
      isNew: true,
    });
    setError('');
  }, [open, examCategories]);

  const create = useMutation({
    mutationFn: () =>
      createAdminResult({
        title: form.title.trim(),
        organization: form.organization.trim(),
        category: form.category,
        examCategory: form.examCategory,
        publishedAt: form.publishedAt,
        isNew: form.isNew,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-results'] });
      onClose();
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'Failed to create result');
    },
  });

  if (!open) return null;

  return (
    <div className="wfx-admin-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="wfx-admin-modal glass-surface"
        role="dialog"
        aria-labelledby="create-result-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="wfx-admin-modal-head">
          <div>
            <p className="wfx-admin-breadcrumb">Publish result</p>
            <h2 id="create-result-title"><Trophy size={20} /> New exam result</h2>
          </div>
        </header>
        <form
          className="wfx-admin-form"
          onSubmit={(e) => {
            e.preventDefault();
            setError('');
            create.mutate();
          }}
        >
          <label className="wfx-admin-field">
            <span>Title</span>
            <input required value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
          </label>
          <label className="wfx-admin-field">
            <span>Organization</span>
            <input required value={form.organization} onChange={(e) => setForm((f) => ({ ...f, organization: e.target.value }))} />
          </label>
          <div className="wfx-admin-form-row">
            <label className="wfx-admin-field">
              <span>Category</span>
              <select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}>
                {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </label>
            <label className="wfx-admin-field">
              <span>Exam category</span>
              <select value={form.examCategory} onChange={(e) => setForm((f) => ({ ...f, examCategory: e.target.value }))}>
                {examCategories.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </label>
          </div>
          <label className="wfx-admin-field">
            <span>Published date</span>
            <input
              type="date"
              required
              value={form.publishedAt}
              onChange={(e) => setForm((f) => ({ ...f, publishedAt: e.target.value }))}
            />
          </label>
          <label className="wfx-admin-checkbox">
            <input
              type="checkbox"
              checked={form.isNew}
              onChange={(e) => setForm((f) => ({ ...f, isNew: e.target.checked }))}
            />
            Mark as new result
          </label>
          {error && <p className="wfx-admin-form-error">{error}</p>}
          <div className="wfx-admin-modal-actions">
            <button type="button" className="secondary-action" onClick={onClose}>Cancel</button>
            <button type="submit" className="primary-action" disabled={create.isPending}>
              {create.isPending ? 'Publishing…' : 'Publish result'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ResultDetailCard({
  resultId,
  examCategories,
  onClose,
}: {
  resultId: string | null;
  examCategories: string[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin-result', resultId],
    queryFn: () => fetchAdminResult(resultId!),
    enabled: Boolean(resultId),
  });

  const update = useMutation({
    mutationFn: (body: Partial<AdminResultRow>) => patchAdminResult(resultId!, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-results'] });
      void qc.invalidateQueries({ queryKey: ['admin-result', resultId] });
    },
  });

  const row = data?.result;

  return (
    <AdminDetailCard
      open={Boolean(resultId)}
      onClose={onClose}
      ariaLabel="Result details"
      breadcrumb="Exam result"
      title={row?.title ?? 'Loading…'}
      illustration="admin.module.results"
      subtitle={
        row ? (
          <>
            <AdminStatusBadge status={row.category} />
            {row.isNew && (
              <span className="wfx-admin-result-new">
                <Sparkles size={14} aria-hidden="true" /> New
              </span>
            )}
            <span className="wfx-muted wfx-admin-subline">{row.organization}</span>
          </>
        ) : undefined
      }
    >
      {isLoading && <LoadingState label="Loading result…" />}
      {isError && <ErrorState message="Could not load result." onRetry={() => void refetch()} />}

      {row && (
        <>
          <dl className="wfx-admin-meta-list wfx-admin-meta-list--grid">
            <div><dt>Exam category</dt><dd>{row.examCategory}</dd></div>
            <div><dt>Published</dt><dd>{row.publishedAt ? new Date(row.publishedAt).toLocaleDateString() : '—'}</dd></div>
            <div><dt>ID</dt><dd>{row.id}</dd></div>
            <div>
              <dt>Public page</dt>
              <dd>
                <Link to={`/results/${row.id}`} className="secondary-action compact" target="_blank">
                  View result
                  <ExternalLink size={12} aria-hidden="true" />
                </Link>
              </dd>
            </div>
          </dl>

          <div className="wfx-admin-result-flags">
            {row.hasScoreCard && <span className="chip-pill">Score card</span>}
            {row.hasMeritList && <span className="chip-pill">Merit list</span>}
            {row.hasCutoff && <span className="chip-pill">Cutoff</span>}
            {row.hasCounselling && <span className="chip-pill">Counselling</span>}
          </div>

          <div className="wfx-admin-drawer-columns">
            <div className="wfx-admin-drawer-section">
              <h3>Core details</h3>
              <div className="wfx-admin-form">
                <label className="wfx-admin-field">
                  <span>Title</span>
                  <input
                    defaultValue={row.title}
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      if (value && value !== row.title) update.mutate({ title: value });
                    }}
                  />
                </label>
                <label className="wfx-admin-field">
                  <span>Organization</span>
                  <input
                    defaultValue={row.organization}
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      if (value && value !== row.organization) update.mutate({ organization: value });
                    }}
                  />
                </label>
                <div className="wfx-admin-form-row">
                  <label className="wfx-admin-field">
                    <span>Category</span>
                    <select
                      defaultValue={row.category}
                      onChange={(e) => update.mutate({ category: e.target.value })}
                    >
                      {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </label>
                  <label className="wfx-admin-field">
                    <span>Exam category</span>
                    <select
                      defaultValue={row.examCategory}
                      onChange={(e) => update.mutate({ examCategory: e.target.value })}
                    >
                      {examCategories.map((cat) => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </label>
                </div>
                <label className="wfx-admin-field">
                  <span>Published date</span>
                  <input
                    type="date"
                    defaultValue={row.publishedAt?.slice(0, 10) ?? ''}
                    onBlur={(e) => {
                      const value = e.target.value;
                      if (value && value !== row.publishedAt?.slice(0, 10)) {
                        update.mutate({ publishedAt: value });
                      }
                    }}
                  />
                </label>
                <button
                  type="button"
                  className="secondary-action compact"
                  disabled={update.isPending}
                  onClick={() => update.mutate({ isNew: !row.isNew })}
                >
                  {row.isNew ? (
                    <><ToggleRight size={16} aria-hidden="true" /> Clear new flag</>
                  ) : (
                    <><ToggleLeft size={16} aria-hidden="true" /> Mark as new</>
                  )}
                </button>
              </div>
            </div>

            <div className="wfx-admin-drawer-section">
              <h3>Official links</h3>
              <div className="wfx-admin-form">
                <label className="wfx-admin-field">
                  <span>Result URL</span>
                  <input
                    defaultValue={row.resultUrl ?? ''}
                    placeholder="https://"
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      if (value !== (row.resultUrl ?? '')) update.mutate({ resultUrl: value || null });
                    }}
                  />
                </label>
                <label className="wfx-admin-field">
                  <span>Official website</span>
                  <input
                    defaultValue={row.officialWebsite ?? ''}
                    placeholder="https://"
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      if (value !== (row.officialWebsite ?? '')) {
                        update.mutate({ officialWebsite: value || null });
                      }
                    }}
                  />
                </label>
                <label className="wfx-admin-field">
                  <span>Official notice</span>
                  <textarea
                    rows={4}
                    className="wfx-admin-textarea"
                    defaultValue={row.officialNotice ?? ''}
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      if (value !== (row.officialNotice ?? '')) {
                        update.mutate({ officialNotice: value || null });
                      }
                    }}
                  />
                </label>
              </div>

              <h3>Artifacts</h3>
              <div className="wfx-admin-result-toggle-grid">
                {([
                  ['hasScoreCard', 'Score card'],
                  ['hasMeritList', 'Merit list'],
                  ['hasCutoff', 'Cutoff'],
                  ['hasCounselling', 'Counselling'],
                ] as const).map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    className={`chip-pill${row[key] ? ' is-active' : ''}`}
                    disabled={update.isPending}
                    onClick={() => update.mutate({ [key]: !row[key] })}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </AdminDetailCard>
  );
}

function ResultCard({
  row,
  onSelect,
}: {
  row: AdminResultRow;
  onSelect: () => void;
}) {
  return (
    <button type="button" className="wfx-admin-result-card glass-surface" onClick={onSelect}>
      <div className="wfx-admin-result-card-head">
        <AdminStatusBadge status={row.category} />
        {row.isNew && (
          <span className="wfx-admin-result-new">
            <Sparkles size={14} aria-hidden="true" /> New
          </span>
        )}
      </div>
      <strong>{row.title}</strong>
      <span className="wfx-muted wfx-admin-result-org">
        <Building2 size={14} aria-hidden="true" /> {row.organization}
      </span>
      <div className="wfx-admin-page-card-stats">
        <span>{row.examCategory}</span>
        <span>{row.publishedAt ? new Date(row.publishedAt).toLocaleDateString() : '—'}</span>
      </div>
    </button>
  );
}

export function AdminResultsScreen() {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [examFilter, setExamFilter] = useState('all');
  const [newFilter, setNewFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    setPage(1);
  }, [debouncedQuery, categoryFilter, examFilter, newFilter]);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin-results', debouncedQuery, categoryFilter, examFilter, newFilter, page],
    queryFn: () =>
      fetchAdminResults({
        q: debouncedQuery || undefined,
        category: categoryFilter,
        examCategory: examFilter,
        isNew: newFilter,
        page,
        limit: 12,
      }),
  });

  const rows = data?.data ?? [];
  const stats = data?.stats;
  const examCategories = data?.examCategories ?? [];
  const categories = data?.categories ?? Object.keys(CATEGORY_LABELS);
  const pagination = data?.pagination;

  const kpiItems = useMemo(
    () => [
      { label: 'Total results', value: stats?.total ?? 0 },
      { label: 'New flagged', value: stats?.new ?? 0 },
      { label: 'Central', value: stats?.central ?? 0 },
      { label: 'State', value: stats?.state ?? 0 },
    ],
    [stats],
  );

  if (isLoading && !data) return <LoadingState label="Loading results…" />;
  if (isError) return <ErrorState message="Could not load results." onRetry={() => void refetch()} />;

  return (
    <>
      <AdminPageLayout
        breadcrumb="Admin / Results"
        title="Results"
        description="Publish and manage exam results — categories, organizations, official links, and new flags."
        icon={ClipboardList}
        illustration="admin.module.results"
        actions={(
          <button type="button" className="primary-action compact" onClick={() => setCreateOpen(true)}>
            <Plus size={16} aria-hidden="true" />
            New result
          </button>
        )}
      >
        <AdminKpiStrip items={kpiItems} />

        <div className="wfx-admin-blog-grid">
          {rows.map((row) => (
            <ResultCard key={row.id} row={row} onSelect={() => setSelectedId(row.id)} />
          ))}
        </div>
        {rows.length === 0 && <AdminEmptyState message="No results match your filters." />}

        <AdminTablePanel
          title="All results"
          toolbar={(
            <div className="wfx-admin-toolbar">
              <AdminSearchInput
                value={query}
                onChange={setQuery}
                placeholder="Search title, organization, or id…"
              />
              <select
                className="wfx-admin-select"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                aria-label="Filter by category"
              >
                <option value="all">All categories</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>{categoryLabel(cat)}</option>
                ))}
              </select>
              <select
                className="wfx-admin-select"
                value={examFilter}
                onChange={(e) => setExamFilter(e.target.value)}
                aria-label="Filter by exam category"
              >
                <option value="all">All exams</option>
                {examCategories.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              <select
                className="wfx-admin-select"
                value={newFilter}
                onChange={(e) => setNewFilter(e.target.value)}
                aria-label="Filter by new flag"
              >
                <option value="all">All results</option>
                <option value="true">New only</option>
                <option value="false">Not new</option>
              </select>
            </div>
          )}
          footer={
            pagination && pagination.pages > 1 ? (
              <div className="wfx-admin-pagination">
                <button
                  type="button"
                  className="secondary-action compact"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  <ChevronLeft size={16} aria-hidden="true" />
                  Previous
                </button>
                <span className="wfx-muted">
                  Page {pagination.page} of {pagination.pages} · {pagination.total} results
                </span>
                <button
                  type="button"
                  className="secondary-action compact"
                  disabled={page >= pagination.pages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                  <ChevronRight size={16} aria-hidden="true" />
                </button>
              </div>
            ) : undefined
          }
        >
          <table className="wfx-admin-table">
            <thead>
              <tr><th>Title</th><th>Organization</th><th>Category</th><th>Exam</th><th>Published</th><th>New</th></tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.id}
                  className="wfx-admin-table-row-clickable"
                  onClick={() => setSelectedId(row.id)}
                >
                  <td><strong>{row.title}</strong></td>
                  <td>{row.organization}</td>
                  <td>{categoryLabel(row.category)}</td>
                  <td>{row.examCategory}</td>
                  <td>{row.publishedAt ? new Date(row.publishedAt).toLocaleDateString() : '—'}</td>
                  <td>{row.isNew ? 'Yes' : 'No'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </AdminTablePanel>
      </AdminPageLayout>

      <CreateResultModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        examCategories={examCategories}
      />
      <ResultDetailCard
        resultId={selectedId}
        examCategories={examCategories}
        onClose={() => setSelectedId(null)}
      />
    </>
  );
}
